import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useConversations } from "@/hooks/useConversations"
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages"
import { ConversationsList } from "@/components/messages/ConversationsList"
import { ChatInterface } from "@/components/messages/ChatInterface"
import { BroadcastModal } from "@/components/messages/BroadcastModal"
import { UserDropdown } from "@/components/messages/UserDropdown"
import { PorteiroDropdown } from "@/components/messages/PorteiroDropdown"
import { Layout } from "@/components/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { MessageCircle, Users, Shield, Phone, Plus, ChevronDown } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useCreateDirectConversation } from "@/hooks/useDirectConversation"
import { toast } from "sonner"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"

type ConversationForList = {
  id: string
  title?: string
  conversation_type: string
  participants: Array<{
    profiles: {
      id: string
      full_name: string
      role: string
    } | null
  }>
  last_message?: {
    content: string
    created_at: string
    sender: {
      full_name: string
    }
  }
  unread_count: number
  updated_at: string
}

export default function Mensagens() {
  const { profile } = useAuth()
  const { data: conversations = [], isLoading } = useConversations()
  const [selectedConversation, setSelectedConversation] = useState<any>(null)
  const [showBroadcast, setShowBroadcast] = useState(false)
  const createConversationMutation = useCreateDirectConversation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  
  // Hook para tempo real - ativado apenas aqui no componente principal
  useRealtimeMessages()

  // Função para obter o nome correto do destinatário
  const getRecipientName = (conversation: any) => {
    if (conversation.title && !conversation.title.startsWith('Conversa com ')) {
      return conversation.title
    }
    
    // Para conversas diretas, encontrar o outro participante
    const otherParticipant = conversation.participants?.find((p: any) => 
      p.profiles?.id !== profile?.id
    )
    
    return otherParticipant?.profiles?.full_name || 'Conversa'
  }

  // Efeito para abrir conversa automaticamente via query parameter
  useEffect(() => {
    const conversationId = searchParams.get('conversation')
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === conversationId)
      if (conversation) {
        setSelectedConversation({
          id: conversation.id,
          title: getRecipientName(conversation)
        })
        // Limpar o query parameter após abrir a conversa
        setSearchParams({})
      }
    }
  }, [conversations, searchParams, profile?.id, setSearchParams])

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // Marcar conversa como inativa
      const { error } = await supabase
        .from('conversations')
        .update({ is_active: false })
        .eq('id', conversationId)

      if (error) throw error

      // Invalidar o cache para forçar re-fetch imediato
      queryClient.invalidateQueries({ queryKey: ["conversations", profile?.id] })

      toast.success("Conversa excluída com sucesso")
      
      // Se a conversa deletada estava selecionada, voltar para a lista
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
      }
    } catch (error) {
      console.error("Erro ao excluir conversa:", error)
      toast.error("Erro ao excluir conversa")
    }
  }

  const handleConversationCreated = (conversationId: string, recipientName: string) => {
    setSelectedConversation({ id: conversationId, title: recipientName })
  }

  const handleConversationSelect = (conversation: any) => {
    setSelectedConversation({
      id: conversation.id,
      title: getRecipientName(conversation)
    })
  }
  const handleCreateConversation = async (targetRole: 'sindico' | 'porteiro') => {
    console.log('=== INICIANDO CRIAÇÃO DE CONVERSA ===')
    console.log('Target role:', targetRole)
    console.log('Profile atual:', profile)
    
    if (!profile?.condominium_id) {
      console.error('Usuário não está associado a um condomínio')
      toast.error("Usuário não está associado a um condomínio")
      return
    }

    try {
      console.log(`Buscando ${targetRole} no condomínio:`, profile.condominium_id)
      
      // Buscar usuário do tipo específico no mesmo condomínio
      const { data: targetUsers, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, is_active, condominium_id')
        .eq('condominium_id', profile.condominium_id)
        .eq('role', targetRole)
        .eq('is_active', true)
        
      console.log(`Resultado da busca ${targetRole}:`, { targetUsers, error })

      if (error) {
        console.error('Erro ao buscar usuário:', error)
        toast.error(`Erro ao buscar ${targetRole}: ${error.message}`)
        return
      }

      if (!targetUsers || targetUsers.length === 0) {
        console.error(`Nenhum ${targetRole} encontrado`)
        toast.error(`Nenhum ${targetRole === 'sindico' ? 'síndico' : 'porteiro'} ativo encontrado no condomínio`)
        return
      }

      // Pegar o primeiro usuário encontrado
      const targetUser = targetUsers[0]
      console.log('Usuário selecionado para conversa:', targetUser)
      
      console.log('Chamando createConversationMutation com:', {
        participant_user_id: targetUser.id,
        title: targetUser.full_name
      })
      
      const conversationId = await createConversationMutation.mutateAsync({
        participant_user_id: targetUser.id
      })
      
      console.log('Conversa criada com ID:', conversationId)
      
      // Abrir conversa criada
      setSelectedConversation({
        id: conversationId,
        title: targetUser.full_name
      })
      
      toast.success(`Conversa iniciada com ${targetUser.full_name}`)
    } catch (error) {
      console.error("=== ERRO AO CRIAR CONVERSA ===", error)
      toast.error(`Erro ao iniciar conversa: ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    }
  }

  return (
    <Layout>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <p>Carregando mensagens...</p>
        </div>
      ) : (
        <>
          {/* Interface específica para cada tipo de usuário */}
          {profile?.role === 'morador' && (
            <>
              {selectedConversation ? (
                <ChatInterface
                  conversationId={selectedConversation.id}
                  recipientName={selectedConversation.title || 'Conversa'}
                  onBack={() => setSelectedConversation(null)}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5" />
                        Mensagens
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {conversations.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p className="text-muted-foreground mb-4">
                            Nenhuma conversa ainda
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Inicie uma nova conversa usando os botões abaixo
                          </p>
                        </div>
                      ) : (
                        <ConversationsList
                          conversations={conversations as unknown as ConversationForList[]}
                          onConversationSelect={handleConversationSelect}
                          currentUserId={profile?.id}
                          onDeleteConversation={handleDeleteConversation}
                        />
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        Ações Rápidas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full justify-start h-16"
                        onClick={() => handleCreateConversation('sindico')}
                      >
                        <Shield className="h-6 w-6 mr-4" />
                        <div className="text-left">
                          <p className="font-medium">Síndico</p>
                          <p className="text-sm text-muted-foreground">
                            Falar com o síndico do condomínio
                          </p>
                        </div>
                      </Button>
                      
                      <PorteiroDropdown onConversationCreated={handleConversationCreated} />
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}

          {/* Interface para porteiros */}
          {profile?.role === 'porteiro' && (
            <>
              {selectedConversation ? (
                <ChatInterface
                  conversationId={selectedConversation.id}
                  recipientName={selectedConversation.title || 'Conversa'}
                  onBack={() => setSelectedConversation(null)}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5" />
                        Conversas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {conversations.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p className="text-muted-foreground mb-4">
                            Nenhuma conversa ainda
                          </p>
                          <p className="text-sm text-muted-foreground">
                            As conversas aparecerão aqui quando moradores ou o síndico entrarem em contato
                          </p>
                        </div>
                      ) : (
                        <ConversationsList
                          conversations={conversations as unknown as ConversationForList[]}
                          onConversationSelect={handleConversationSelect}
                          currentUserId={profile?.id}
                          onDeleteConversation={handleDeleteConversation}
                        />
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        Ações Rápidas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <UserDropdown onConversationCreated={handleConversationCreated}>
                        <Button
                          variant="outline"
                          size="lg"
                          className="w-full justify-between h-14"
                        >
                          <div className="flex items-center">
                            <MessageCircle className="h-5 w-5 mr-3" />
                            <div className="text-left">
                              <p className="font-medium">Nova Conversa</p>
                              <p className="text-sm text-muted-foreground">
                                Falar com morador ou síndico
                              </p>
                            </div>
                          </div>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </UserDropdown>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}

          {/* Interface para síndico e administração */}
          {(profile?.role === 'sindico' || profile?.role === 'administracao') && (
            <>
              {selectedConversation ? (
                <ChatInterface
                  conversationId={selectedConversation.id}
                  recipientName={selectedConversation.title || 'Conversa'}
                  onBack={() => setSelectedConversation(null)}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ConversationsList
                    conversations={conversations as unknown as ConversationForList[]}
                    onConversationSelect={handleConversationSelect}
                    currentUserId={profile?.id}
                    showBroadcastOption={profile?.role === 'sindico'}
                    onBroadcastClick={() => setShowBroadcast(true)}
                    onDeleteConversation={handleDeleteConversation}
                  />
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        Ações Rápidas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <UserDropdown onConversationCreated={handleConversationCreated}>
                        <Button
                          variant="outline"
                          size="lg"
                          className="w-full justify-between h-14"
                        >
                          <div className="flex items-center">
                            <MessageCircle className="h-5 w-5 mr-3" />
                            <div className="text-left">
                              <p className="font-medium">Nova Conversa</p>
                              <p className="text-sm text-muted-foreground">
                                Falar com morador ou portaria
                              </p>
                            </div>
                          </div>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </UserDropdown>
                      {profile?.role === 'sindico' && (
                        <Button
                          variant="outline"
                          size="lg"
                          className="w-full justify-start h-14"
                          onClick={() => setShowBroadcast(true)}
                        >
                          <Users className="h-5 w-5 mr-3" />
                          <div className="text-left">
                            <p className="font-medium">Comunicado Geral</p>
                            <p className="text-sm text-muted-foreground">
                              Enviar para todos os moradores
                            </p>
                          </div>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
          
          <BroadcastModal
            open={showBroadcast}
            onOpenChange={setShowBroadcast}
          />
        </>
      )}
    </Layout>
  )
}